import React, { useEffect, useState } from "react";
import AppLayout from "../components/AppLayout";
import { api, formatApiErrorDetail } from "../lib/api";
import { FloppyDisk, CheckCircle, Calendar } from "@phosphor-icons/react";

/**
 * Work coach settings — currently only the Calendly embed snippet rendered on
 * each coachee's brain-type view so they can directly book a coaching session.
 */
export default function JobCoachSettingsPage() {
  const [embed, setEmbed] = useState("");
  const [initialEmbed, setInitialEmbed] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/settings");
        setEmbed(data.calendly_embed || "");
        setInitialEmbed(data.calendly_embed || "");
      } finally { setLoading(false); }
    })();
  }, []);

  const dirty = embed !== initialEmbed;

  const onSave = async () => {
    setError(""); setSaving(true);
    try {
      await api.put("/me/settings", { calendly_embed: embed });
      setInitialEmbed(embed);
      setSavedAt(new Date());
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <AppLayout title="Instellingen" breadcrumbs={[{ label: "Dashboard" }, { label: "Instellingen" }]}>
      <div className="max-w-3xl space-y-8">
        <section className="neuro-card-bold p-6 bg-white">
          <div className="flex items-start gap-3 mb-5">
            <div className="inline-flex h-10 w-10 items-center justify-center bg-[#1E88E5] text-white rounded-sm">
              <Calendar size={20} weight="bold" />
            </div>
            <div>
              <h2 className="font-display text-xl font-bold tracking-tight">Calendly-koppeling</h2>
              <p className="text-sm text-zinc-500 mt-1 leading-relaxed">
                Plak hier de embed-code van jouw Calendly-pagina. Deze wordt aan jouw coachees getoond
                op hun &lsquo;Mijn breintype&rsquo;-pagina en direct na het voltooien van de online test,
                zodat ze een afspraak met jou kunnen inplannen.
              </p>
            </div>
          </div>

          <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-1.5">Embed-code</label>
          <textarea
            value={embed}
            onChange={(e) => setEmbed(e.target.value)}
            rows={10}
            spellCheck={false}
            data-testid="settings-calendly-input"
            placeholder={
              '<!-- Calendly inline widget begin -->\n<div class="calendly-inline-widget" data-url="https://calendly.com/...?hide_event_type_details=1&hide_gdpr_banner=1" style="min-width:320px;height:700px;"></div>\n<script type="text/javascript" src="https://assets.calendly.com/assets/external/widget.js" async></script>\n<!-- Calendly inline widget end -->'
            }
            className="w-full border border-zinc-300 focus:border-zinc-900 outline-none px-3 py-2 text-sm font-mono leading-relaxed rounded-sm bg-zinc-50/40"
          />

          {error && <div className="mt-3 text-sm text-[#E53935]" data-testid="settings-error">{error}</div>}

          <div className="mt-5 flex items-center gap-3">
            <button
              type="button"
              onClick={onSave}
              disabled={!dirty || saving}
              data-testid="settings-save-button"
              className="inline-flex items-center gap-2 bg-zinc-900 text-white px-5 py-2 text-sm font-semibold tracking-wide uppercase rounded-sm hover:bg-[#1E88E5] disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
            >
              <FloppyDisk size={16} weight="bold" /> {saving ? "Opslaan..." : "Opslaan"}
            </button>
            {savedAt && !dirty && (
              <span className="inline-flex items-center gap-1 text-xs text-[#43A047]" data-testid="settings-saved">
                <CheckCircle size={14} weight="fill" /> Opgeslagen om {savedAt.toLocaleTimeString()}
              </span>
            )}
            {embed && (
              <button
                type="button"
                onClick={() => setEmbed("")}
                data-testid="settings-clear-button"
                className="text-xs text-zinc-500 hover:text-[#E53935] underline-offset-2 hover:underline"
              >
                Embed verwijderen
              </button>
            )}
          </div>
        </section>

        <section>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Status</div>
          <div className="neuro-card bg-white p-6" data-testid="settings-calendly-status">
            {loading ? (
              <div className="text-zinc-500 text-sm">Laden...</div>
            ) : embed && embed.trim() ? (
              <div className="flex items-start gap-3">
                <CheckCircle size={20} weight="fill" className="text-[#43A047] mt-0.5 shrink-0" />
                <div className="text-sm text-zinc-700">
                  <div className="font-medium">Calendly embed is ingesteld.</div>
                  <div className="text-zinc-500 mt-1">
                    Coachees zien de boekingswidget op hun breintype-pagina en aan het einde van hun
                    online test.
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-sm text-zinc-500" data-testid="settings-calendly-empty">
                Nog geen embed ingesteld. Plak je Calendly inline-widget-code hierboven en klik op Opslaan.
              </div>
            )}
          </div>
        </section>
      </div>
    </AppLayout>
  );
}
